import { Button } from "components/DesignSystem/Button/Button";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { Cross } from "components/icons/Cross";
import { useState } from "react";
import { useGetSupportingDocumentTagsQuery } from "store/apis/documents";
import { stopPropagation } from "utils/stopPropagation";

type DocumentTagMultiSelectProps = {
  groupId: string;
  selectedUuids: string[];
  onChange: (uuids: string[]) => void;
};

export const DocumentTagMultiSelect = ({
  groupId,
  selectedUuids,
  onChange,
}: DocumentTagMultiSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const { data: tagOptions = [] } = useGetSupportingDocumentTagsQuery(
    { groupId },
    { skip: !groupId }
  );

  const getTagLabel = (uuid: string) =>
    (() => {
      const tag = tagOptions.find((option) => option.uuid === uuid);
      return tag?.label || tag?.title || uuid;
    })();

  const handleToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { checked, value } = e.target;

    if (checked) {
      onChange([...selectedUuids, value]);
      return;
    }

    onChange(selectedUuids.filter((uuid) => uuid !== value));
  };

  const handleRemove = (uuid: string) => {
    onChange(selectedUuids.filter((selectedUuid) => selectedUuid !== uuid));
  };

  return (
    <Dropdown.Root open={isOpen} onOpenChange={setIsOpen}>
      <Dropdown.Trigger asChild>
        <div className="t-flex t-cursor-pointer t-flex-wrap t-items-center t-gap-1.5">
          {selectedUuids.map((uuid) => (
            <Tag key={uuid} tagType="gray" icon={false} size="small">
              <div className="t-flex t-items-center t-gap-1">
                <span>{getTagLabel(uuid)}</span>
                <Button
                  customType="ghost_icon"
                  size="xxsmall"
                  className="hover:!t-bg-neutral-10"
                  onPointerDown={stopPropagation}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemove(uuid);
                  }}
                >
                  <Cross size="12" />
                </Button>
              </div>
            </Tag>
          ))}

          <Button
            size="small"
            customType="link"
            onClick={(e) => e.stopPropagation()}
          >
            + Add
          </Button>
        </div>
      </Dropdown.Trigger>

      <Dropdown.Portal>
        <Dropdown.Content sideOffset={6} align="start" className="t-w-52">
          {tagOptions.map((tag) => (
            <Dropdown.Item
              key={tag.uuid}
              className="t-flex t-gap-1.5 hover:!t-bg-surface-transparent"
              onSelect={(e) => e.preventDefault()}
              textValue=""
            >
              <Checkbox
                name={tag.uuid}
                value={tag.uuid}
                label={tag.label || tag.title}
                onChange={handleToggle}
                checked={selectedUuids.includes(tag.uuid)}
              />
            </Dropdown.Item>
          ))}
        </Dropdown.Content>
      </Dropdown.Portal>
    </Dropdown.Root>
  );
};
